'use client';
import React, { useState, useMemo, useEffect } from 'react';
import { Filter, SlidersHorizontal, ChevronDown, X, Download, Eye, FileText, User, Calendar, Building2, Loader2 } from 'lucide-react';
import ThesisDetailModal from './ThesisDetailModal';
import { thesisService } from '@/lib/services/thesisService';

export interface ThesisRecord {
  id: string;
  title_primary: string;
  title_secondary: string;
  author_surname: string;
  author_name: string;
  supervisor_surname: string;
  supervisor_name: string;
  department: string;
  school: string;
  doc_type: 'master' | 'phd';
  date_submission: string;
  date_publication: string;
  abstract_primary: string;
  abstract_secondary: string;
  thesis_keywords: { keyword_label: string; keyword_area: string }[];
  primary_lang: string;
  access_rights: string;
  license: string;
  pid: string;
  orcid: string;
  committee_member_1: string;
  committee_member_2: string;
  committee_member_3: string;
  pdf_en_path: string;
  pdf_el_path: string;
  institution: string;
}

const YEARS = ['All Years', '2026', '2025', '2024', '2023', '2022', '2021'];
const TYPES = ['All Types', 'PhD Dissertation', "Master's Thesis"];
const LANGUAGES = ['All Languages', 'English', 'Greek', 'Bilingual'];

export default function ThesisBrowser() {
  const [theses, setTheses] = useState<ThesisRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('All Departments');
  const [filterYear, setFilterYear] = useState('All Years');
  const [filterType, setFilterType] = useState('All Types');
  const [filterLang, setFilterLang] = useState('All Languages');
  const [filterAccess, setFilterAccess] = useState('all');
  const [selectedThesis, setSelectedThesis] = useState<ThesisRecord | null>(null);
  const [showFilters, setShowFilters] = useState(true);
  const [sortBy, setSortBy] = useState<'date' | 'title' | 'author'>('date');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [displayLang, setDisplayLang] = useState<'en' | 'el'>('en');

  useEffect(() => {
    loadTheses();
  }, []);

  const loadTheses = async () => {
    setLoading(true);
    try {
      const data = await thesisService.getPublished();
      setTheses(data);
    } catch (err) {
      console.log('Load published theses error:', err);
    } finally {
      setLoading(false);
    }
  };

  const uniqueDepts = ['All Departments', ...Array.from(new Set(theses.map(t => t.department).filter(Boolean)))];

  const filtered = useMemo(() => {
    return theses.filter((t) => {
      const q = search.toLowerCase();
      const title = displayLang === 'en' ? t.title_primary : (t.title_secondary || t.title_primary);
      const author = `${t.author_surname} ${t.author_name}`;
      const keywords = t.thesis_keywords?.map(k => k.keyword_label).join(' ') || '';
      const matchSearch = !q ||
        title?.toLowerCase().includes(q) ||
        author?.toLowerCase().includes(q) ||
        keywords.toLowerCase().includes(q) ||
        t.department?.toLowerCase().includes(q);
      const matchDept = filterDept === 'All Departments' || t.department === filterDept;
      const year = t.date_publication?.slice(0, 4) || t.date_submission?.slice(0, 4) || '';
      const matchYear = filterYear === 'All Years' || year === filterYear;
      const matchType = filterType === 'All Types' || (filterType === 'PhD Dissertation' ? t.doc_type === 'phd' : t.doc_type === 'master');
      const matchLang = filterLang === 'All Languages' ||
        (filterLang === 'English' && t.primary_lang === 'en') ||
        (filterLang === 'Greek' && t.primary_lang === 'el') ||
        (filterLang === 'Bilingual' && t.title_secondary);
      const matchAccess = filterAccess === 'all' || t.access_rights === filterAccess;
      return matchSearch && matchDept && matchYear && matchType && matchLang && matchAccess;
    }).sort((a, b) => {
      if (sortBy === 'date') return (b.date_publication || b.date_submission || '').localeCompare(a.date_publication || a.date_submission || '');
      if (sortBy === 'title') return (a.title_primary || '').localeCompare(b.title_primary || '');
      if (sortBy === 'author') return a.author_surname.localeCompare(b.author_surname);
      return 0;
    });
  }, [theses, search, filterDept, filterYear, filterType, filterLang, filterAccess, sortBy, displayLang]);

  const activeFilterCount = [
    filterDept !== 'All Departments',
    filterYear !== 'All Years',
    filterType !== 'All Types',
    filterLang !== 'All Languages',
    filterAccess !== 'all',
  ].filter(Boolean).length;

  const clearFilters = () => {
    setFilterDept('All Departments');
    setFilterYear('All Years');
    setFilterType('All Types');
    setFilterLang('All Languages');
    setFilterAccess('all');
    setSearch('');
  };

  const getTitle = (t: ThesisRecord) => displayLang === 'en' ? t.title_primary : (t.title_secondary || t.title_primary);
  const getAuthor = (t: ThesisRecord) => `${t.author_surname}, ${t.author_name}`;
  const getAbstract = (t: ThesisRecord) => displayLang === 'en' ? t.abstract_primary : (t.abstract_secondary || t.abstract_primary);
  const getYear = (t: ThesisRecord) => t.date_publication?.slice(0, 4) || t.date_submission?.slice(0, 4) || '—';

  if (loading) {
    return (
      <div className="max-w-screen-2xl mx-auto px-6 xl:px-10 2xl:px-16 py-20 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={32} className="animate-spin text-[#1a2e5a]" />
          <p className="text-sm text-[#4a5578]">Loading thesis repository...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-screen-2xl mx-auto px-6 xl:px-10 2xl:px-16 py-10">
      {/* Browser header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-700 text-[#0f1a35]">Browse Theses</h2>
          <p className="text-sm text-[#4a5578] mt-0.5">
            Showing <span className="font-600 text-[#1a2e5a]">{filtered.length}</span> of {theses.length} published theses
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Display language toggle */}
          <div className="flex items-center border border-[#e2e6f0] rounded-lg overflow-hidden text-xs font-600">
            <button onClick={() => setDisplayLang('en')} className={`px-3 py-2 transition-all ${displayLang === 'en' ? 'bg-[#1a2e5a] text-white' : 'bg-white text-[#4a5578] hover:bg-gray-50'}`}>EN</button>
            <button onClick={() => setDisplayLang('el')} className={`px-3 py-2 transition-all ${displayLang === 'el' ? 'bg-[#1a2e5a] text-white' : 'bg-white text-[#4a5578] hover:bg-gray-50'}`}>ΕΛ</button>
          </div>

          <div className="relative">
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value as 'date' | 'title' | 'author')}
              className="appearance-none border border-[#e2e6f0] rounded-lg px-3 py-2 text-xs font-500 text-[#4a5578] bg-white pr-7 focus:outline-none focus:border-[#1a2e5a] cursor-pointer">
              <option value="date">Sort: Date</option>
              <option value="title">Sort: Title</option>
              <option value="author">Sort: Author</option>
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8892a8] pointer-events-none" />
          </div>

          <div className="flex items-center border border-[#e2e6f0] rounded-lg overflow-hidden">
            <button onClick={() => setViewMode('grid')} className={`p-2 transition-all ${viewMode === 'grid' ? 'bg-[#1a2e5a] text-white' : 'bg-white text-[#4a5578] hover:bg-gray-50'}`} title="Grid view">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="0" y="0" width="6" height="6" rx="1" fill="currentColor" /><rect x="8" y="0" width="6" height="6" rx="1" fill="currentColor" /><rect x="0" y="8" width="6" height="6" rx="1" fill="currentColor" /><rect x="8" y="8" width="6" height="6" rx="1" fill="currentColor" /></svg>
            </button>
            <button onClick={() => setViewMode('list')} className={`p-2 transition-all ${viewMode === 'list' ? 'bg-[#1a2e5a] text-white' : 'bg-white text-[#4a5578] hover:bg-gray-50'}`} title="List view">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="0" y="0" width="14" height="2.5" rx="1" fill="currentColor" /><rect x="0" y="5.5" width="14" height="2.5" rx="1" fill="currentColor" /><rect x="0" y="11" width="14" height="2.5" rx="1" fill="currentColor" /></svg>
            </button>
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 border rounded-lg px-3 py-2 text-xs font-600 transition-all ${showFilters ? 'border-[#1a2e5a] bg-[#1a2e5a]/5 text-[#1a2e5a]' : 'border-[#e2e6f0] text-[#4a5578] hover:border-[#1a2e5a]'}`}
          >
            <SlidersHorizontal size={13} />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-[#c9a84c] text-[#111e3d] text-xs font-700 w-4 h-4 rounded-full flex items-center justify-center">{activeFilterCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div className="relative mb-4">
        <Filter size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8892a8]" />
        <input
          type="text"
          placeholder="Search by title, author, keyword, or department..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-11 pr-4 py-3 border border-[#e2e6f0] rounded-xl bg-white text-sm focus:outline-none focus:border-[#1a2e5a] focus:ring-1 focus:ring-[#1a2e5a]/10 transition-all"
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8892a8] hover:text-[#4a5578]">
            <X size={14} />
          </button>
        )}
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="bg-white border border-[#e2e6f0] rounded-xl p-4 mb-5 animate-fade-in">
          <div className="flex flex-wrap gap-3 items-center">
            <select value={filterDept} onChange={(e) => setFilterDept(e.target.value)} className="text-xs border border-[#e2e6f0] rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-[#1a2e5a]">
              {uniqueDepts.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} className="text-xs border border-[#e2e6f0] rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-[#1a2e5a]">
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="text-xs border border-[#e2e6f0] rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-[#1a2e5a]">
              {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={filterLang} onChange={(e) => setFilterLang(e.target.value)} className="text-xs border border-[#e2e6f0] rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-[#1a2e5a]">
              {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
            <select value={filterAccess} onChange={(e) => setFilterAccess(e.target.value)} className="text-xs border border-[#e2e6f0] rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-[#1a2e5a]">
              <option value="all">All Access</option>
              <option value="open">Open Access</option>
              <option value="restricted">Restricted</option>
              <option value="embargoed">Embargoed</option>
            </select>
            {activeFilterCount > 0 && (
              <button onClick={clearFilters} className="flex items-center gap-1.5 text-xs text-[#8892a8] hover:text-red-500 transition-colors">
                <X size={12} /> Clear all
              </button>
            )}
          </div>
        </div>
      )}

      {/* Results */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white border border-[#e2e6f0] rounded-2xl text-center">
          <FileText size={40} className="text-[#c0c8d8] mb-4" />
          <h3 className="text-lg font-600 text-[#0f1a35] mb-2">No theses found</h3>
          <p className="text-sm text-[#4a5578] mb-4">Try adjusting your search or filters.</p>
          <button onClick={clearFilters} className="text-xs text-[#1a2e5a] hover:underline font-600">Clear all filters</button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((t) => (
            <div
              key={t.id}
              className="bg-white border border-[#e2e6f0] rounded-2xl p-5 hover:shadow-md hover:border-[#1a2e5a]/20 transition-all cursor-pointer group"
              onClick={() => setSelectedThesis(t)}
            >
              <div className="flex items-start justify-between mb-3">
                <span className={t.doc_type === 'phd' ? 'badge-phd' : 'badge-master'}>
                  {t.doc_type === 'phd' ? 'PhD' : "Master's"}
                </span>
                <div className="flex items-center gap-1.5">
                  {t.pdf_en_path && <span className="text-xs bg-blue-50 text-blue-600 border border-blue-100 rounded px-1.5 py-0.5 font-600">EN</span>}
                  {t.pdf_el_path && <span className="text-xs bg-amber-50 text-amber-600 border border-amber-100 rounded px-1.5 py-0.5 font-600">ΕΛ</span>}
                </div>
              </div>
              <h3 className="text-sm font-700 text-[#0f1a35] leading-snug mb-2 line-clamp-2 group-hover:text-[#1a2e5a] transition-colors">
                {getTitle(t)}
              </h3>
              <div className="space-y-1 mb-3">
                <div className="flex items-center gap-1.5 text-xs text-[#4a5578]">
                  <User size={11} className="text-[#8892a8]" />
                  <span className="truncate">{getAuthor(t)}</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-[#4a5578]">
                  <Building2 size={11} className="text-[#8892a8]" />
                  <span className="truncate">{t.department}</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-[#4a5578]">
                  <Calendar size={11} className="text-[#8892a8]" />
                  <span>{getYear(t)}</span>
                </div>
              </div>
              <p className="text-xs text-[#8892a8] leading-relaxed line-clamp-2 mb-3">{getAbstract(t)}</p>
              <div className="flex flex-wrap gap-1">
                {t.thesis_keywords?.slice(0, 3).map((kw) => (
                  <span key={`kw-${kw.keyword_label}`} className="text-xs bg-[#f7f8fc] border border-[#e2e6f0] text-[#4a5578] rounded-full px-2 py-0.5">{kw.keyword_label}</span>
                ))}
                {(t.thesis_keywords?.length || 0) > 3 && (
                  <span className="text-xs text-[#8892a8]">+{(t.thesis_keywords?.length || 0) - 3}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((t) => (
            <div
              key={t.id}
              className="bg-white border border-[#e2e6f0] rounded-xl px-5 py-4 hover:shadow-sm hover:border-[#1a2e5a]/20 transition-all cursor-pointer flex items-center gap-4"
              onClick={() => setSelectedThesis(t)}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${t.doc_type === 'phd' ? 'bg-purple-50 border border-purple-200' : 'bg-blue-50 border border-blue-200'}`}>
                <FileText size={16} className={t.doc_type === 'phd' ? 'text-purple-600' : 'text-blue-600'} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={t.doc_type === 'phd' ? 'badge-phd' : 'badge-master'}>{t.doc_type === 'phd' ? 'PhD' : "Master's"}</span>
                  <span className="text-xs text-[#8892a8]">{getYear(t)}</span>
                </div>
                <h3 className="text-sm font-600 text-[#0f1a35] truncate">{getTitle(t)}</h3>
                <div className="flex items-center gap-3 text-xs text-[#8892a8] mt-0.5">
                  <span>{getAuthor(t)}</span>
                  <span>·</span>
                  <span>{t.department}</span>
                </div>
              </div>
              <div className="flex-shrink-0 flex items-center gap-1.5">
                {t.pdf_en_path && <span className="text-xs bg-blue-50 text-blue-600 border border-blue-100 rounded px-1.5 py-0.5 font-600">EN</span>}
                {t.pdf_el_path && <span className="text-xs bg-amber-50 text-amber-600 border border-amber-100 rounded px-1.5 py-0.5 font-600">ΕΛ</span>}
                <Eye size={14} className="text-[#8892a8] ml-2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {selectedThesis && (
        <ThesisDetailModal
          thesis={selectedThesis}
          displayLang={displayLang}
          onClose={() => setSelectedThesis(null)}
        />
      )}
    </div>
  );
}